import { orderBy } from "lodash";
import ApiClient from "shared/service/api/ApiClient";
import {
    Client,
    PeriodDefinition_PublicSelectionMode,
    ReportConfig_Public,
    SalesReportConfigV2_Public,
    SegmentDefinition_PublicSelectionMode,
} from "shared/service/api/generated";
import { SearchEntityTypeEnum } from "view360Card/models";
import {
    Company,
    IntermediaryNumber,
    PeriodDefinition,
    ReportDate,
    SelectionTypeEnum,
    Segment,
    Mode,
    Year,
    CompanyReportSectionConfig,
    Scope,
} from "../models";
import { GlobalReportConfig } from "../models/GlobalReportConfig";
import { ReportConfig } from "../models/ReportConfig";
import { SegmentDefinition } from "../models/SegmentDefinition";
import { CompanyBasedSectionReports } from "../models/report-config/CompanyBasedSectionReports";
import SalesOverviewSectionReports from "../models/report-config/SalesOverviewSectionReports";
import SalesAndActivitySectionReports from "../models/report-config/SalesAndActivitySectionReports";
import OtherParametersSectionReports from "../models/report-config/OtherParametersSectionReports";
import CustomerSectionReports from "../models/report-config/CustomerSectionReports";

class ReportsConfigService {
    private apiClient: Client;

    constructor(apiClient: Client) {
        this.apiClient = apiClient;
    }

    async getReportConfig(entityId: number, entityType: SearchEntityTypeEnum): Promise<GlobalReportConfig> {
        let dtoResponse: SalesReportConfigV2_Public;
        if (entityType === SearchEntityTypeEnum.Intermediary) {
            dtoResponse = await this.apiClient.v360_reports_intermediary_config_v2(entityId);
        } else if (entityType === SearchEntityTypeEnum.SalesPerson) {
            dtoResponse = await this.apiClient.v360_reports_salesperson_config_v2(entityId);
        } else {
            throw new Error("Unsupported entity type");
        }

        const salesOverviewSectionConfigList = this.getCompanyBasedSectionConfigList(
            dtoResponse,
            SalesOverviewSectionReports
        );

        const salesAndActivitySectionConfigList = this.getCompanyBasedSectionConfigList(
            dtoResponse,
            SalesAndActivitySectionReports
        );

        const customersSectionConfigList = this.getCompanyBasedSectionConfigList(dtoResponse, CustomerSectionReports);

        const otherParametersSectionConfigList = this.getCompanyBasedSectionConfigList(
            dtoResponse,
            OtherParametersSectionReports
        );

        const reportDateList: ReportDate[] = [];
        const allTenants = orderBy(dtoResponse.tenantList, (t) => t.orderIndex);
        allTenants.forEach((tenant) => {
            const reportTimestamp = dtoResponse.reportDateInformationList?.find((r) => r.companyCode === tenant.code)
                ?.timestamp;
            reportDateList.push(
                new ReportDate(
                    tenant.code ?? "",
                    tenant.name ?? "",
                    reportTimestamp ? new Date(reportTimestamp) : undefined
                )
            );
        });

        const intermediaryList = (dtoResponse.intermediaryList ?? []).map((i) => {
            return new IntermediaryNumber(Math.random(), i.code ?? "", i.description ?? "");
        });
        const scopeList = this.getScopesFromPatternReport(
            dtoResponse.reportList,
            SalesOverviewSectionReports.patternReports
        );
        const result = new GlobalReportConfig(
            salesOverviewSectionConfigList,
            salesAndActivitySectionConfigList,
            customersSectionConfigList,
            otherParametersSectionConfigList,
            reportDateList,
            intermediaryList,
            scopeList
        );

        return result;
    }

    private getScopesFromPatternReport(reportList: ReportConfig_Public[] | undefined, patternReportNames: string[]) {
        let patternReport = reportList?.find((r) => patternReportNames.findIndex((p) => p === r.report) > -1);
        if (patternReport) {
            return orderBy(patternReport.reportScopeList ?? [], (i) => i.selected === true, "desc").map((s) => {
                return new Scope(s.code ?? "", s.description ?? "", s.selected ?? false);
            });
        }
        return [];
    }

    private getCompanyBasedSectionConfigList(
        dtoResponse: SalesReportConfigV2_Public,
        companyBasedSectionReports: CompanyBasedSectionReports
    ) {
        const result: CompanyReportSectionConfig[] = [];

        const allTenants = orderBy(dtoResponse.tenantList, (t) => t.orderIndex);
        allTenants.forEach((tenant) => {
            const reportsInSection = (dtoResponse.reportList ?? []).filter(
                (r) => companyBasedSectionReports.allReports.findIndex((i) => i === r.report) > -1
            );
            const reportsForThisTenant = reportsInSection?.filter(
                (r) => (r.tenantList ?? []).map((t) => t.code?.toString()).findIndex((c) => c === tenant.code) > -1
            );
            if (reportsForThisTenant && reportsForThisTenant.length > 0) {
                let patternReport = reportsForThisTenant.find(
                    (r) => companyBasedSectionReports.patternReports.findIndex((p) => p === r.report) > -1
                );
                if (!patternReport) {
                    patternReport = reportsForThisTenant[0];
                }
                const segmentDefinitionDto = patternReport.tenantList?.find((t) => t.code === tenant.code)
                    ?.segmentDefinition;
                const segmentDefinition = segmentDefinitionDto
                    ? new SegmentDefinition(
                          (segmentDefinitionDto.list ?? []).map(
                              (s) => new Segment(s.code ?? "", s.description ?? "", s.selected ?? false)
                          ),
                          segmentDefinitionDto.selectionMode === SegmentDefinition_PublicSelectionMode.MULTIPLE
                              ? SelectionTypeEnum.MULTIPLE
                              : SelectionTypeEnum.SINGLE
                      )
                    : undefined;
                const company = new Company(
                    tenant.code ?? "",
                    tenant.name ?? "",
                    tenant.colorScheme,
                    segmentDefinition
                );
                const periodDefinition = new PeriodDefinition(
                    (patternReport.periodDefinition?.periodList ?? []).map(
                        (p) => new Year(p.code ?? "", p.description ?? "", p.selected ?? false)
                    ),
                    patternReport.periodDefinition?.selectionMode === PeriodDefinition_PublicSelectionMode.MULTIPLE
                        ? SelectionTypeEnum.MULTIPLE
                        : SelectionTypeEnum.SINGLE,
                    patternReport.periodDefinition?.required ?? false
                );
                const modes = (patternReport.reportModeList ?? []).map(
                    (m) => new Mode(m.code ?? "", m.description ?? "", m.selected ?? false)
                );
                const reports = reportsForThisTenant.map((r) => new ReportConfig(r.report?.toString() ?? ""));
                const config = new CompanyReportSectionConfig(company, periodDefinition, modes, reports);
                result.push(config);
            }
        });
        return result;
    }
}

export default new ReportsConfigService(ApiClient);
